import { Fragment, useMemo, useState } from "react";
import { useStytchMember } from "@stytch/react/b2b";
import moment from "moment-timezone";
import { useListGrainSamplesActive, GrainPosition } from "@/lib/api/grainPositions";
import { cn, CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { formatNumberToDisplay } from "@/lib/api/common";
import { CalendarIcon, Loader, ChevronDown, ChevronUp, DownloadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import Decimal from "decimal.js";
import { DataTable } from "@/components/ui/virtualizedTable";
import { createColumnHelper } from "@tanstack/react-table";
import { Commodities, get_groupable_commodity_display_name } from "@/lib/api/commodity";
import { downloadCsvTextAsFile } from "@/lib/browser/download";

export function GrainPositionDashboard() {
    const { member } = useStytchMember();
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [showDetails, setShowDetails] = useState(false);
    
    // Format date as YYYY-MM-DD for API
    const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
    
    const grainPositionsRes = useListGrainSamplesActive({ 
        date: formattedDate 
    });

    const positions = grainPositionsRes?.data?.data?.data || [];
    const isLoading = grainPositionsRes.isLoading;
    const isRefetching = grainPositionsRes.isFetching && !grainPositionsRes.isLoading;
    const error = grainPositionsRes.error;

    // Group positions by commodity
    const groupedPositions = useMemo(() => {
        if (!positions) { return {}; }

        const groups: Record<string, GrainPosition[]> = {};
        
        positions.forEach((position: GrainPosition) => {
            const commodity = position.commodity_group_type || "Unknown";
            if (!groups[commodity]) {
                groups[commodity] = [];
            }
            groups[commodity].push(position);
        });

        return groups;
    }, [positions]);

    const handleToday = () => {
        setSelectedDate(new Date());
    };

    const handleYesterday = () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        setSelectedDate(yesterday);
    };

    const handleDownloadCsv = () => {
        const csvRows: string[] = [];
        
        // Add header
        csvRows.push("Commodity,Quantity,Comments");
        csvRows.push(""); // Empty row after header
        
        // Process each commodity group
        const commodityEntries = Object.entries(groupedPositions);
        commodityEntries.forEach(([commodity, commodityPositions], index) => {
            const displayName = get_groupable_commodity_display_name(commodity as Commodities);
            
            // Group positions by comments within this commodity (same logic as in CommodityCard)
            const groupedByComments: Record<string, { totalQuantity: Decimal; positions: GrainPosition[] }> = {};
            
            commodityPositions.forEach((position) => {
                const key = position.comments || "";
                
                if (!groupedByComments[key]) {
                    groupedByComments[key] = {
                        totalQuantity: new Decimal(0),
                        positions: []
                    };
                }
                
                const quantity = new Decimal(position.derived_quantity || 0);
                const adjustedQuantity = position.buy_sell?.toLowerCase() === "s" 
                    ? quantity.negated() 
                    : quantity;
                
                groupedByComments[key].totalQuantity = groupedByComments[key].totalQuantity.plus(adjustedQuantity);
                groupedByComments[key].positions.push(position);
            });
            
            // Sort by total quantity (descending) like in the cards
            const sortedGroups = Object.entries(groupedByComments).sort((a, b) => 
                b[1].totalQuantity.minus(a[1].totalQuantity).toNumber()
            );
            
            // Add data rows for this commodity
            sortedGroups.forEach(([comments, group]) => {
                const formattedQuantity = group.totalQuantity.toString();
                
                // Handle commas in values by wrapping in quotes if necessary
                const quotedComments = comments.includes(",") ? `"${comments}"` : comments;
                const quotedCommodity = displayName.includes(",") ? `"${displayName}"` : displayName;
                
                csvRows.push(`${quotedCommodity},${formattedQuantity},${quotedComments}`);
            });
            
            // Add empty rows between commodity sections (except after the last one)
            if (index < commodityEntries.length - 1) {
                csvRows.push("");
                csvRows.push("");
            }
        });
        
        // Join all rows and download
        const csvContent = csvRows.join("\n");
        const filename = `grain_positions_${formattedDate}.csv`;
        downloadCsvTextAsFile(filename, csvContent);
    };

    return (
        <section className="flex min-h-full flex-1 flex-col justify-start px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
                <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
                    Grain Positions
                </h1>
            </div>

            <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleToday}
                        className={cn(
                            format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd") && "bg-primary text-primary-foreground"
                        )}
                    >
                        Today
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleYesterday}
                        className={cn(
                            format(selectedDate, "yyyy-MM-dd") === format(new Date(Date.now() - 86400000), "yyyy-MM-dd") && "bg-primary text-primary-foreground"
                        )}
                    >
                        Yesterday
                    </Button>
                </div>
                
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" className="w-[240px] justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(selectedDate, "PPP")}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(date) => date && setSelectedDate(date)}
                            initialFocus
                        />
                    </PopoverContent>
                </Popover>

                <div className="text-sm text-muted-foreground">
                    API Date: {formattedDate}
                </div>

                {isRefetching && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        <span>Refreshing data...</span>
                    </div>
                )}
                
                <div className="ml-auto">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadCsv}
                        disabled={!positions.length || isLoading}
                    >
                        <DownloadCloud className="mr-2 h-4 w-4" />
                        Download CSV
                    </Button>
                </div>
            </div>

            {error ? (
                <Card>
                    <CardContent className="pt-6">
                        <p className="text-center text-red-500">Error loading grain positions. Please try again.</p>
                    </CardContent>
                </Card>
            ) : isLoading && !positions.length ? (
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex flex-col items-center justify-center gap-2">
                            <Loader className="h-4 w-4 animate-spin" />
                            <p className="text-center text-muted-foreground">Loading grain positions...</p>
                        </div>
                    </CardContent>
                </Card>
            ) : Object.keys(groupedPositions).length === 0 ? (
                <Card>
                    <CardContent className="pt-6">
                        <p className="text-center text-muted-foreground">No grain positions found for {formattedDate}</p>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {Object.entries(groupedPositions).map(([commodity, commodityPositions]) => (
                            <CommodityCard
                                key={commodity}
                                commodity={commodity}
                                positions={commodityPositions}
                            />
                        ))}
                    </div>
                    
                    {positions.length > 0 && (
                        <Card className="mt-6">
                            <CardHeader className="py-3">
                                <button
                                    onClick={() => setShowDetails(!showDetails)}
                                    className="flex items-center justify-between w-full hover:opacity-80 text-left"
                                >
                                    <h3 className="text-lg font-semibold">Trade Details</h3>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-muted-foreground">
                                            {positions.length} total trades
                                        </span>
                                        {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    </div>
                                </button>
                            </CardHeader>
                            {showDetails && (
                                <CardContent className="pt-0">
                                    <TradeDetailsTable positions={positions} />
                                </CardContent>
                            )}
                        </Card>
                    )}
                </>
            )}
        </section>
    );
}

interface CommodityCardProps {
    commodity: string;
    positions: GrainPosition[];
}

interface AggregatedPosition {
    comments: string;
    totalQuantity: Decimal;
    positions: GrainPosition[];
}

function CommodityCard({ commodity: commodity_group_type, positions }: CommodityCardProps) {
    // Aggregate positions by comments
    const aggregatedPositions = useMemo(() => {
        const grouped: Record<string, AggregatedPosition> = {};
        
        positions.forEach((position) => {
            const key = position.comments || "";
            
            if (!grouped[key]) {
                grouped[key] = {
                    comments: key,
                    totalQuantity: new Decimal(0),
                    positions: []
                };
            }
            
            const quantity = new Decimal(position.derived_quantity || 0);
            const adjustedQuantity = position.buy_sell?.toLowerCase() === "s" 
                ? quantity.negated() 
                : quantity;
            
            grouped[key].totalQuantity = grouped[key].totalQuantity.plus(adjustedQuantity);
            grouped[key].positions.push(position);
        });
        
        // Convert to array and sort by total quantity (descending)
        return Object.values(grouped).sort((a, b) => 
            b.totalQuantity.minus(a.totalQuantity).toNumber()
        );
    }, [positions]);

    // Calculate total quantity
    const totalQuantity = useMemo(() => {
        return positions.reduce((total, position) => {
            const quantity = new Decimal(position.derived_quantity || 0);
            // Make quantity negative if it's a sell
            const adjustedQuantity = position.buy_sell?.toLowerCase() === "s" 
                ? quantity.negated() 
                : quantity;
            return total.plus(adjustedQuantity);
        }, new Decimal(0));
    }, [positions]);

    // Determine card color based on commodity
    const getCardColor = (commodity: string) => {
        const commodityLower = commodity.toLowerCase();
        if (commodityLower.includes("corn")) return "border-yellow-200 bg-yellow-50";
        if (commodityLower.includes("wheat")) return "border-orange-200 bg-orange-50";
        if (commodityLower.includes("soy") || commodityLower.includes("bean")) return "border-green-200 bg-green-50";
        if (commodityLower.includes("canola")) return "border-purple-200 bg-purple-50";
        return "border-gray-200 bg-gray-50";
    };

    const formatQuantity = (value: string | number | Decimal) => {
        return formatNumberToDisplay(value, { emptyFormat: "--", zeroFormat: "--"});
    };

    const commodity: string = get_groupable_commodity_display_name(commodity_group_type as Commodities);

    return (
        <Card className={cn("h-full", getCardColor(commodity))}>
            <CardHeader className="pb-2 pt-4">
                <div className="flex items-baseline justify-between">
                    <CardTitle className="text-base font-bold">{commodity}</CardTitle>
                    <span className="text-sm font-semibold">{formatQuantity(totalQuantity)}</span>
                </div>
            </CardHeader>
            <CardContent className="space-y-1">
                {aggregatedPositions.map((aggregated, index) => {
                    const hasMultiplePositions = aggregated.positions.length > 1;
                    const isNegative = aggregated.totalQuantity.isNegative();
                    
                    return (
                        <div key={index} className="border-t pt-1 pb-1 first:border-t-0 first:pt-0">
                            <div className="flex items-baseline gap-2">
                                <span className={cn(
                                    "text-sm font-semibold min-w-[80px] text-right",
                                    isNegative ? "text-red-600" : "text-green-600"
                                )}>
                                    {formatQuantity(aggregated.totalQuantity)}
                                </span>
                                <span className="text-xs text-muted-foreground flex-1 truncate">
                                    {aggregated.comments}
                                </span>
                                {hasMultiplePositions && (
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                                        ({aggregated.positions.length})
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
}

interface TradeDetailsTableProps {
    positions: GrainPosition[];
}

function TradeDetailsTable({ positions }: TradeDetailsTableProps) {
    const columnHelper = createColumnHelper<GrainPosition>();
    
    const formatQuantity = (value: string | number | Decimal) => {
        return formatNumberToDisplay(value, { emptyFormat: "--", zeroFormat: "--"});
    };
    
    const columns = [
        columnHelper.accessor("commodity_group_type", {
            id: "commodity_group_type",
            header: ({ table }: any) => (
                <>
                    <button
                        onClick={table.getToggleAllRowsExpandedHandler()}
                        className="mr-2"
                    >
                        {table.getIsAllRowsExpanded() ? '▼' : '▶'}
                    </button>
                    Commodity
                </>
            ),
            cell: ({ row, getValue }: any) => (
                <div
                    style={{
                        paddingLeft: `${row.depth * 2}rem`,
                    }}
                    className="flex items-center gap-2"
                >
                    {row.getCanExpand() ? (
                        <button
                            onClick={row.getToggleExpandedHandler()}
                            style={{ cursor: 'pointer' }}
                        >
                            {row.getIsExpanded() ? '▼' : '▶'}
                        </button>
                    ) : null}
                    {row.depth === 0 ? (
                        <span className="font-medium">{getValue() || "Unknown"}</span>
                    ) : row.depth === 1 ? (
                        <span className="text-sm text-muted-foreground">{row.original.comments || "(No comment)"}</span>
                    ) : (
                        <span className="text-sm">Trade #{row.index + 1}</span>
                    )}
                </div>
            ),
            enableGrouping: true,
            aggregationFn: 'unique',
        }),
        columnHelper.accessor("buy_sell", {
            id: "buy_sell",
            header: "Buy/Sell",
            cell: (info) => {
                const isSell = info.getValue()?.toLowerCase() === "s";
                return (
                    <span className={cn(
                        "font-medium",
                        isSell ? "text-red-600" : "text-green-600"
                    )}>
                        {info.getValue() || "-"}
                    </span>
                );
            },
            aggregatedCell: () => <span className="text-muted-foreground">-</span>,
        }),
        columnHelper.accessor("derived_quantity", {
            id: "derived_quantity", 
            header: "Quantity",
            cell: (info) => {
                const quantity = new Decimal(info.getValue() || 0);
                const isSell = info.row.original.buy_sell?.toLowerCase() === "s";
                const adjustedQuantity = isSell ? quantity.negated() : quantity;
                
                return (
                    <div className={cn(
                        "text-right font-semibold",
                        adjustedQuantity.isNegative() ? "text-red-600" : "text-green-600"
                    )}>
                        {formatQuantity(adjustedQuantity)}
                    </div>
                );
            },
            aggregationFn: "sum",
            aggregatedCell: ({ getValue }: any) => {
                const value = getValue();
                const decimalValue = value instanceof Decimal ? value : new Decimal(value || 0);
                return (
                    <div className={cn()}>
                    </div>
                );
            },
        }),
        columnHelper.accessor("broker", {
            id: "broker",
            header: "Broker",
            cell: (info) => info.row.depth > 0 ? info.getValue() || "-" : "",
            aggregatedCell: () => "",
        }),
        columnHelper.accessor("account", {
            id: "account",
            header: "Account",
            cell: (info) => info.row.depth > 0 ? info.getValue() || "-" : "",
            aggregatedCell: () => "",
        }),
        columnHelper.accessor("order_number", {
            id: "order_number",
            header: "Order #",
            cell: (info) => info.row.depth > 0 ? info.getValue() || "-" : "",
            aggregatedCell: () => "",
        }),
    ];
    
    return (
        <DataTable
            defaultSorting={[
                { id: "commodity_group_type", desc: false },
                { id: "comments", desc: false }
            ]}
            defaultGrouping={["commodity_group_type"]}
            columns={columns as any[]}
            data={positions}
            height="600px"
            enableColumnResizing={false}
        />
    );
}
